import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import * as api from '../utils/api';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'metrics' | 'schools' | 'invoices' | 'analytics' | 'verification'>('verification');

  // Data state
  const [metrics, setMetrics] = useState<any>({ users: 0, schools: 0, enquiries: 0, revenue: 0 });
  const [schools, setSchools] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [pendingSchools, setPendingSchools] = useState<any[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'schools') loadSchools();
    if (activeTab === 'invoices') loadInvoices();
    if (activeTab === 'analytics') loadAnalytics();
    if (activeTab === 'verification') loadPendingSchools();
  }, [activeTab]);

  async function loadInitialData() {
    try {
      await loadMetrics();
    } catch (error) {
      console.error('Failed to load data:', error);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function loadMetrics() {
    try {
      const res = await api.getAnalytics();
      setMetrics({
        users: res.metrics?.total_users || 0,
        schools: res.metrics?.total_schools || 0,
        enquiries: res.metrics?.total_enquiries || 0,
        revenue: res.metrics?.total_revenue || 0,
      });
    } catch (error) {
      console.error('Failed to load metrics:', error);
    }
  }

  async function loadSchools() {
    try {
      const res = await api.getSchools();
      setSchools(res.schools || []);
    } catch (error) {
      console.error('Failed to load schools:', error);
    }
  }

  async function loadInvoices() {
    try {
      const res = await api.getInvoices();
      setInvoices(res.invoices || []);
    } catch (error) {
      console.error('Failed to load invoices:', error);
    }
  }

  async function loadAnalytics() {
    try {
      const res = await api.getAnalytics();
      setAnalytics(res);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  }

  async function loadPendingSchools() {
    try {
      const res = await api.getPendingSchools();
      setPendingSchools(res.schools || []);
    } catch (error) {
      console.error('Failed to load pending schools:', error);
      showToast('Failed to load pending schools', 'error');
    }
  }

  async function handleVerifySchool(schoolId: string, notes: string = '') {
    try {
      setActionLoading(schoolId);
      await api.verifySchool(schoolId, notes);
      showToast('School verified successfully', 'success');
      await loadPendingSchools();
    } catch (error) {
      showToast('Failed to verify school', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRejectSchool(schoolId: string, reason: string) {
    if (!reason.trim()) {
      showToast('Please provide a rejection reason', 'error');
      return;
    }
    try {
      setActionLoading(schoolId);
      await api.rejectSchool(schoolId, reason);
      showToast('School verification rejected', 'success');
      await loadPendingSchools();
    } catch (error) {
      showToast('Failed to reject school', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleUpdateSchoolStatus(schoolId: string, verified: boolean) {
    try {
      setActionLoading(schoolId);
      await api.updateSchool(schoolId, { verified: !verified });
      showToast(`School ${verified ? 'unverified' : 'verified'}`, 'success');
      await loadSchools();
    } catch (error) {
      showToast('Failed to update school status', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  const tabs = [
    { id: 'verification', label: 'Verification', icon: '✅', count: pendingSchools.length },
    { id: 'metrics', label: 'Metrics', icon: '📊' },
    { id: 'schools', label: 'Schools', icon: '🏫', count: schools.length },
    { id: 'invoices', label: 'Invoices', icon: '💰', count: invoices.filter(i => i.status === 'pending').length },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">IndieSchools - Admin Portal</h1>
              <p className="text-sm text-gray-600">Welcome, {user?.email}</p>
            </div>
            <button onClick={logout} className="btn btn-secondary">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4">
          <nav className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* VERIFICATION TAB */}
        {activeTab === 'verification' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">School Verification Queue</h2>
              <div className="text-sm text-gray-600">
                {pendingSchools.length} pending {pendingSchools.length === 1 ? 'school' : 'schools'}
              </div>
            </div>

            {pendingSchools.length === 0 ? (
              <div className="card text-center py-12">
                <div className="text-4xl mb-4">✅</div>
                <p className="text-gray-600">No schools pending verification</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingSchools.map((school) => (
                  <div key={school.id} className="card">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-900">{school.name}</h3>
                            <p className="text-sm text-gray-600">
                              {school.email} • {school.first_name} {school.last_name}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Registered: {new Date(school.user_created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {school.contract_signed ? (
                              <span className="badge badge-success text-xs">Contract Signed</span>
                            ) : (
                              <span className="badge badge-warning text-xs">No Contract</span>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-3 rounded-lg">
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-medium">URN</p>
                            <p className="text-sm text-gray-900 mt-1 font-mono">
                              {school.urn || <span className="text-red-600">Not provided</span>}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-medium">Website</p>
                            {school.website ? (
                              <a
                                href={school.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-primary-600 hover:underline mt-1 block truncate"
                              >
                                {school.website}
                              </a>
                            ) : (
                              <p className="text-sm text-red-600 mt-1">Not provided</p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-medium">Location</p>
                            <p className="text-sm text-gray-900 mt-1">
                              {school.city}, {school.postcode}
                            </p>
                          </div>
                        </div>

                        {school.verification_notes && (
                          <div className="mt-3 bg-yellow-50 border-l-4 border-yellow-400 p-3">
                            <p className="text-sm text-yellow-800">
                              <strong>Notes:</strong> {school.verification_notes}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 md:min-w-[200px]">
                        <button
                          onClick={() => handleVerifySchool(school.id)}
                          disabled={actionLoading === school.id || !school.contract_signed || !school.urn}
                          className="btn btn-success text-sm"
                        >
                          {actionLoading === school.id ? 'Processing...' : '✓ Verify School'}
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Enter reason for rejection:');
                            if (reason) handleRejectSchool(school.id, reason);
                          }}
                          disabled={actionLoading === school.id}
                          className="btn btn-danger text-sm"
                        >
                          ✗ Reject
                        </button>
                        {(!school.contract_signed || !school.urn) && (
                          <p className="text-xs text-red-600 mt-1">
                            Missing: {!school.contract_signed && 'Contract'} {!school.contract_signed && !school.urn && '&'} {!school.urn && 'URN'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* METRICS TAB */}
        {activeTab === 'metrics' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Platform Metrics</h2>
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="card">
                <p className="text-sm text-gray-600 mb-1">Total Users</p>
                <p className="text-3xl font-bold text-primary-600">{metrics.users}</p>
                <p className="text-xs text-gray-500 mt-1">Registered on platform</p>
              </div>
              <div className="card">
                <p className="text-sm text-gray-600 mb-1">Total Schools</p>
                <p className="text-3xl font-bold text-primary-600">{metrics.schools}</p>
                <p className="text-xs text-gray-500 mt-1">Active listings</p>
              </div>
              <div className="card">
                <p className="text-sm text-gray-600 mb-1">Total Enquiries</p>
                <p className="text-3xl font-bold text-primary-600">{metrics.enquiries}</p>
                <p className="text-xs text-gray-500 mt-1">All time</p>
              </div>
              <div className="card">
                <p className="text-sm text-gray-600 mb-1">Revenue (YTD)</p>
                <p className="text-3xl font-bold text-green-600">
                  £{metrics.revenue.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">Year to date</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="font-semibold mb-4">Recent Enquiries</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">St. Albans Academy</span>
                    <span className="text-gray-500">5 new</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">Greenwood Prep</span>
                    <span className="text-gray-500">3 new</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">Riverside College</span>
                    <span className="text-gray-500">2 new</span>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="font-semibold mb-4">Recent Placements</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">St. Albans Academy</span>
                    <span className="text-green-600">£12,000</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">Elm House</span>
                    <span className="text-green-600">£16,000</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">Riverside College</span>
                    <span className="text-green-600">£14,000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SCHOOLS TAB */}
        {activeTab === 'schools' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Schools</h2>
              <div className="text-sm text-gray-600">
                {schools.length} {schools.length === 1 ? 'school' : 'schools'}
              </div>
            </div>
            {schools.length > 0 ? (
              <div className="card overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr className="text-left">
                      <th className="pb-3 font-medium">School Name</th>
                      <th className="pb-3 font-medium">City</th>
                      <th className="pb-3 font-medium">Type</th>
                      <th className="pb-3 font-medium">Fees</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.map(school => (
                      <tr key={school.id} className="border-b last:border-0">
                        <td className="py-3 font-medium">{school.name}</td>
                        <td className="py-3 text-sm text-gray-600">{school.city}</td>
                        <td className="py-3 text-sm text-gray-600">{school.type}</td>
                        <td className="py-3 text-sm text-gray-600">
                          {school.day_fees ? `£${school.day_fees.toLocaleString()}` : '-'}
                        </td>
                        <td className="py-3">
                          <span className={`badge badge-${school.verified ? 'success' : 'warning'}`}>
                            {school.verified ? 'Verified' : 'Pending'}
                          </span>
                        </td>
                        <td className="py-3">
                          <button
                            onClick={() => handleUpdateSchoolStatus(school.id, school.verified)}
                            disabled={actionLoading === school.id}
                            className="btn btn-sm btn-secondary"
                          >
                            {school.verified ? 'Unverify' : 'Verify'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="card text-center py-12">
                <p className="text-gray-600">No schools registered yet</p>
              </div>
            )}
          </div>
        )}

        {/* INVOICES TAB */}
        {activeTab === 'invoices' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Invoices</h2>
              <div className="text-sm text-gray-600">
                {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'}
              </div>
            </div>
            {invoices.length > 0 ? (
              <div className="card overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr className="text-left">
                      <th className="pb-3 font-medium">Invoice ID</th>
                      <th className="pb-3 font-medium">School</th>
                      <th className="pb-3 font-medium">Amount</th>
                      <th className="pb-3 font-medium">Type</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(invoice => (
                      <tr key={invoice.id} className="border-b last:border-0">
                        <td className="py-3 text-sm font-mono">{invoice.id.substring(0, 8)}</td>
                        <td className="py-3 font-medium">{invoice.school_name}</td>
                        <td className="py-3 text-sm font-semibold text-primary-600">
                          £{invoice.amount.toLocaleString()}
                        </td>
                        <td className="py-3 text-sm text-gray-600">{invoice.type}</td>
                        <td className="py-3">
                          <span className={`badge badge-${
                            invoice.status === 'paid' ? 'success' :
                            invoice.status === 'pending' ? 'warning' : 'danger'
                          }`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td className="py-3 text-sm text-gray-600">
                          {new Date(invoice.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="card text-center py-12">
                <p className="text-gray-600">No invoices yet</p>
              </div>
            )}
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Analytics</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Enquiry Trend */}
              <div className="card">
                <h3 className="font-semibold mb-4">Monthly Enquiries</h3>
                {analytics?.enquiries_by_month && analytics.enquiries_by_month.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.enquiries_by_month.map((item: any, index: number) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="text-sm text-gray-600 w-20">{item.month}</div>
                        <div className="flex-1">
                          <div className="bg-gray-200 rounded-full h-6 overflow-hidden">
                            <div
                              className="bg-primary-600 h-full flex items-center justify-end pr-2 text-xs text-white font-medium"
                              style={{
                                width: `${Math.min(100, (item.count / Math.max(...analytics.enquiries_by_month.map((i: any) => i.count))) * 100)}%`
                              }}
                            >
                              {item.count}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600">No data available</p>
                )}
              </div>

              {/* Revenue Trend */}
              <div className="card">
                <h3 className="font-semibold mb-4">Monthly Revenue</h3>
                {analytics?.revenue_by_month && analytics.revenue_by_month.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.revenue_by_month.map((item: any, index: number) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="text-sm text-gray-600 w-20">{item.month}</div>
                        <div className="flex-1">
                          <div className="bg-gray-200 rounded-full h-6 overflow-hidden">
                            <div
                              className="bg-green-600 h-full flex items-center justify-end pr-2 text-xs text-white font-medium"
                              style={{
                                width: `${Math.min(100, (item.amount / Math.max(...analytics.revenue_by_month.map((i: any) => i.amount))) * 100)}%`
                              }}
                            >
                              £{item.amount.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600">No data available</p>
                )}
              </div>

              {/* Curriculum Distribution */}
              <div className="card">
                <h3 className="font-semibold mb-4">Curriculum Distribution</h3>
                {schools.length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(
                      schools.reduce((acc: any, school) => {
                        acc[school.curriculum] = (acc[school.curriculum] || 0) + 1;
                        return acc;
                      }, {})
                    ).map(([curriculum, count]: [string, any]) => (
                      <div key={curriculum} className="flex items-center gap-3">
                        <div className="text-sm text-gray-700 w-24">{curriculum}</div>
                        <div className="flex-1">
                          <div className="bg-gray-200 rounded-full h-6 overflow-hidden">
                            <div
                              className="bg-blue-600 h-full flex items-center justify-end pr-2 text-xs text-white font-medium"
                              style={{
                                width: `${(count / schools.length) * 100}%`
                              }}
                            >
                              {count}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 w-12 text-right">
                          {Math.round((count / schools.length) * 100)}%
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600">No data available</p>
                )}
              </div>

              {/* Top Schools by Enquiries */}
              <div className="card">
                <h3 className="font-semibold mb-4">Top Schools by Enquiries</h3>
                {analytics?.top_schools && analytics.top_schools.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.top_schools.map((school: any, index: number) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="text-sm text-gray-700 flex-1 truncate">{school.name}</div>
                        <div className="flex items-center gap-2">
                          <div className="bg-gray-200 rounded-full h-6 w-24 overflow-hidden">
                            <div
                              className="bg-purple-600 h-full flex items-center justify-end pr-2 text-xs text-white font-medium"
                              style={{
                                width: `${Math.min(100, (school.enquiry_count / Math.max(...analytics.top_schools.map((s: any) => s.enquiry_count))) * 100)}%`
                              }}
                            >
                              {school.enquiry_count}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600">No data available</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
